import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Search, Filter, Calendar, ArrowUpRight, ArrowDownLeft, Plus, Trash2, TrendingUp, DollarSign, Wallet, AlertTriangle, CheckCircle, XCircle, FileText, CreditCard, Download, Pencil } from 'lucide-react';
import { useAppData } from '../hooks/useAppData';
import { useNotification } from '../context/NotificationContext';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { ToggleGroup, ToggleGroupItem } from './ui/toggle-group';
import dayjs from 'dayjs';
import { TableFilterBar } from './ui/TableFilterBar';
import { TableExportMenu } from './ui/TableExportMenu';
import { TablePeriodSelector } from './ui/TablePeriodSelector';
import { matchesMonthYear, matchesYear } from '../utils/dateFilters';

// Agrega función utilitaria para formato Guatemala
function formatGTDate(dateStr: string) {
  if (!dateStr) return '-';
  // Fix: Parse manual para evitar problemas de zona horaria
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  }
  // Fallback por si acaso
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

interface MovimientosTableProps {
  abrirNuevoMovimiento?: boolean;
  setAbrirNuevoMovimiento?: (v: boolean) => void;
}

export function MovimientosTable({ abrirNuevoMovimiento, setAbrirNuevoMovimiento }: MovimientosTableProps) {
  const { expenses, addMovimiento, deleteMovimiento, updateMovimiento, sellers, lots } = useAppData();
  const { showNotification } = useNotification();
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('Todos');
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [movimientoToDelete, setMovimientoToDelete] = useState<string | null>(null);
  const [payEgresoOpen, setPayEgresoOpen] = useState(false);
  const [egresoToPay, setEgresoToPay] = useState<any>(null);
  const [payForm, setPayForm] = useState({ fechaPago: dayjs().format('YYYY-MM-DD'), referencia: '' });
  const [payError, setPayError] = useState<string | null>(null);

  // Filtros de fecha
  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState('Todos');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const meses = ['Todos', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  // Estado para el formulario de nuevo movimiento
  const [newMovimiento, setNewMovimiento] = useState({
    tipo: 'ingreso', // ingreso, egreso
    concepto: '',
    monto: '',
    fecha: dayjs().format('YYYY-MM-DD'),
    responsable: 'Administrador',
    referencia: '', // Opcional
    estado: 'Pagado', // Pagado, Pendiente (solo para egresos)
    lotes: [] as string[], // Para vincular a lotes
    vendedor: '', // Para vincular a vendedor
    comprador: '', // Para vincular a comprador
    numeroDocumento: '', // Nuevo campo
    transferencia: '' // Nuevo campo
  });
  const [formError, setFormError] = useState<string | null>(null);

  // Efecto para abrir el modal desde props externas
  React.useEffect(() => {
    if (abrirNuevoMovimiento) {
      setModalOpen(true);
      // Resetear el prop para evitar bucles, si la función existe
      if (setAbrirNuevoMovimiento) {
        setAbrirNuevoMovimiento(false);
      }
    }
  }, [abrirNuevoMovimiento, setAbrirNuevoMovimiento]);

  // Ordenar movimientos por fecha descendente
  const sortedMovimientos = [...expenses].sort((a, b) => {
    return new Date(b.fecha).getTime() - new Date(a.fecha).getTime();
  });

  // Filtrar movimientos
  const filteredMovimientos = sortedMovimientos.filter(mov => {
    const searchLower = search.toLowerCase();
    const matchesSearch =
      mov.concepto.toLowerCase().includes(searchLower) ||
      mov.responsable.toLowerCase().includes(searchLower) ||
      (mov.referencia && mov.referencia.toLowerCase().includes(searchLower)) ||
      (mov.numeroDocumento && mov.numeroDocumento.toLowerCase().includes(searchLower)) ||
      (mov.transferencia && mov.transferencia.toLowerCase().includes(searchLower));
    const matchesTipo = tipoFilter === 'Todos' || mov.tipo === tipoFilter.toLowerCase();

    // Filtrado por mes y año mejorado
    let matchesMonthFilter = true;
    if (selectedMonth === 'Todos') {
      matchesMonthFilter = matchesYear(mov.fecha, selectedYear);
    } else {
      matchesMonthFilter = matchesMonthYear(mov.fecha, selectedMonth, selectedYear);
    }

    return matchesSearch && matchesTipo && matchesMonthFilter;
  });

  // Calcular totales
  const totalIngresos = filteredMovimientos
    .filter(m => ['ingreso', 'enganche', 'contado', 'abono'].includes(m.tipo))
    .reduce((sum, m) => sum + parseFloat(m.monto), 0);

  const totalEgresos = filteredMovimientos
    .filter(m => m.tipo === 'egreso' || m.tipo === 'comision')
    .reduce((sum, m) => sum + parseFloat(m.monto), 0);

  const balance = totalIngresos - totalEgresos;

  const handleSave = async () => {
    if (!newMovimiento.concepto || !newMovimiento.monto || !newMovimiento.fecha) {
      setFormError('Por favor completa los campos obligatorios.');
      return;
    }

    try {
      // Construir referencia combinada si se usan los nuevos campos
      let referenciaFinal = newMovimiento.referencia;
      if (newMovimiento.numeroDocumento || newMovimiento.transferencia) {
        const partes = [];
        if (newMovimiento.numeroDocumento) partes.push(`No. Doc.: ${newMovimiento.numeroDocumento}`);
        if (newMovimiento.transferencia) partes.push(`No. Auto.: ${newMovimiento.transferencia}`);
        if (newMovimiento.referencia) partes.push(newMovimiento.referencia); // Mantener referencia manual si existe
        referenciaFinal = partes.join(', ');
      }

      await addMovimiento({
        ...newMovimiento,
        monto: parseFloat(newMovimiento.monto),
        referencia: referenciaFinal,
        // Guardar campos individuales también para facilitar búsquedas futuras
        numeroDocumento: newMovimiento.numeroDocumento || null,
        transferencia: newMovimiento.transferencia || null
      });
      setModalOpen(false);
      setNewMovimiento({
        tipo: 'ingreso',
        concepto: '',
        monto: '',
        fecha: dayjs().format('YYYY-MM-DD'),
        responsable: 'Administrador',
        referencia: '',
        estado: 'Pagado',
        lotes: [],
        vendedor: '',
        comprador: '',
        numeroDocumento: '',
        transferencia: ''
      });
      setFormError(null);
      showNotification(`${newMovimiento.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'} registrado: ${newMovimiento.concepto} - Q${parseFloat(newMovimiento.monto).toLocaleString()}`, 'success');
    } catch (e) {
      console.error('Error al guardar movimiento:', e);
      setFormError('Error al guardar el movimiento.');
    }
  };

  const handleDelete = async () => {
    if (movimientoToDelete) {
      try {
        await deleteMovimiento(movimientoToDelete);
        setDeleteConfirmOpen(false);
        setMovimientoToDelete(null);
      } catch (e) {
        console.error('Error al eliminar movimiento:', e);
        alert('Error al eliminar el movimiento.');
      }
    }
  };

  const handlePayEgreso = async () => {
    if (!egresoToPay) return;
    if (!payForm.fechaPago) {
      setPayError('La fecha de pago es obligatoria');
      return;
    }

    try {
      await updateMovimiento(egresoToPay.id, {
        estado: 'Pagado',
        fecha: payForm.fechaPago, // Actualizar fecha a la del pago real
        referencia: payForm.referencia ? `${egresoToPay.referencia || ''} (Pago: ${payForm.referencia})` : egresoToPay.referencia
      });
      setPayEgresoOpen(false);
      setEgresoToPay(null);
      setPayForm({ fechaPago: dayjs().format('YYYY-MM-DD'), referencia: '' });
      setPayError(null);
    } catch (e) {
      console.error('Error al pagar egreso:', e);
      setPayError('Error al registrar el pago.');
    }
  };

  // Exportar a Excel
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredMovimientos.map(mov => ({
      Fecha: mov.fecha,
      Concepto: mov.concepto,
      Tipo: mov.tipo,
      Monto: parseFloat(mov.monto).toLocaleString(),
      Responsable: mov.responsable,
      Referencia: mov.referencia || '-',
      Estado: mov.estado || 'Pagado'
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Movimientos');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    FileSaver.saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'movimientos.xlsx');
  };

  // Exportar a CSV
  const exportToCSV = () => {
    const ws = XLSX.utils.json_to_sheet(filteredMovimientos.map(mov => ({
      Fecha: mov.fecha,
      Concepto: mov.concepto,
      Tipo: mov.tipo,
      Monto: parseFloat(mov.monto).toLocaleString(),
      Responsable: mov.responsable,
      Referencia: mov.referencia || '-',
      Estado: mov.estado || 'Pagado'
    })));
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    FileSaver.saveAs(blob, 'movimientos.csv');
  };

  // Exportar a PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Reporte de Movimientos Financieros', 14, 16);
    autoTable(doc, {
      head: [['Fecha', 'Concepto', 'Tipo', 'Monto', 'Responsable', 'Estado']],
      body: filteredMovimientos.map(mov => [
        mov.fecha,
        mov.concepto,
        mov.tipo,
        parseFloat(mov.monto).toLocaleString(),
        mov.responsable,
        mov.estado || 'Pagado'
      ]),
      startY: 22,
    });
    doc.save('movimientos.pdf');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-foreground">Movimientos Financieros</h2>
          <p className="text-muted-foreground mt-1 text-[13px]">Registro detallado de ingresos y egresos</p>
        </div>
        <Button onClick={() => setModalOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-none border-0">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Movimiento
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6 px-1">
        {/* Buscar */}
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/70" />
          <Input
            placeholder="Buscar movimiento..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 bg-transparent border border-input hover:border-ring focus:border-ring rounded-[6px] h-8 text-[13px] text-foreground placeholder:text-muted-foreground/40 shadow-none transition-colors"
          />
        </div>

        {/* Tipo */}
        <div className="relative">
          <select
            value={tipoFilter}
            onChange={e => setTipoFilter(e.target.value)}
            className="bg-transparent border border-input hover:border-ring focus:border-ring rounded-[6px] h-8 text-[13px] text-foreground shadow-none transition-colors pl-3 pr-8 appearance-none cursor-pointer min-w-[100px]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238A8F98' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.5rem center',
              backgroundSize: '10px'
            }}
          >
            <option>Todos</option>
            <option value="ingreso">Ingresos</option>
            <option value="egreso">Egresos</option>
          </select>
        </div>

        <div className="flex-1" />

        {/* Exportar */}
        <TableExportMenu
          onExcel={exportToExcel}
          onCSV={exportToCSV}
          onPDF={exportToPDF}
        />
      </div>

      {/* Periodo de Consulta */}
      <TablePeriodSelector
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        onMonthChange={setSelectedMonth}
        onYearChange={setSelectedYear}
        months={meses}
        label="Período de Consulta"
      />

      {/* Tarjetas de Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Card className="bg-card border border-emerald-500/20 shadow-none rounded-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Ingresos</CardTitle>
            <TrendingUp className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-500">Q. {totalIngresos.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            <p className="text-xs text-muted-foreground mt-1">Ingresos registrados en el período</p>
          </CardContent>
        </Card>
        <Card className="bg-card border border-red-500/20 shadow-none rounded-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Egresos</CardTitle>
            <ArrowDownLeft className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-500">Q. {totalEgresos.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            <p className="text-xs text-muted-foreground mt-1">Gastos registrados en el período</p>
          </CardContent>
        </Card>
        <Card className="bg-card border border-blue-500/20 shadow-none rounded-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Balance Neto</CardTitle>
            <Wallet className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-500">Q. {balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            <p className="text-xs text-muted-foreground mt-1">Diferencia entre ingresos y egresos</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border-border shadow-none rounded-lg overflow-hidden">
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border/50">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium tracking-tight text-foreground">Movimientos ({filteredMovimientos.length})</span>
              <span className="text-xs text-muted-foreground">Mostrando {filteredMovimientos.length} registros</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b border-border/50">
                  <TableHead className="px-4 py-3 text-[11px] font-medium text-muted-foreground h-10">Orden</TableHead>
                  <TableHead className="px-4 py-3 text-[11px] font-medium text-muted-foreground h-10">No. Autorización</TableHead>
                  <TableHead className="px-4 py-3 text-[11px] font-medium text-muted-foreground h-10">No. Doc.</TableHead>
                  <TableHead className="px-4 py-3 text-[11px] font-medium text-muted-foreground h-10">Concepto</TableHead>
                  <TableHead className="px-4 py-3 text-[11px] font-medium text-muted-foreground h-10">Responsable</TableHead>
                  <TableHead className="px-4 py-3 text-[11px] font-medium text-muted-foreground h-10">Fecha</TableHead>
                  <TableHead className="px-4 py-3 text-[11px] font-medium text-muted-foreground h-10">Ingreso</TableHead>
                  <TableHead className="px-4 py-3 text-[11px] font-medium text-muted-foreground h-10">Egreso</TableHead>
                  <TableHead className="px-4 py-3 text-[11px] font-medium text-muted-foreground h-10">Estado</TableHead>
                  <TableHead className="px-4 py-3 text-[11px] font-medium text-muted-foreground h-10 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMovimientos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      No se encontraron movimientos
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMovimientos.map((mov: any, index: number) => {
                    const isIngreso = ['ingreso', 'enganche', 'contado', 'abono'].includes(mov.tipo);
                    const monto = parseFloat(mov.monto);

                    return (
                      <TableRow key={mov.id} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                        <TableCell className="px-4 py-3 text-[13px] text-muted-foreground">
                          {index + 1}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-[13px] text-foreground font-mono">
                          {mov.transferencia || mov.numeroDocumento || '-'}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-[13px] text-foreground font-mono">
                          {mov.numeroDocumento || '-'}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-[13px] text-foreground max-w-[300px] truncate" title={mov.concepto}>
                          {mov.concepto}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-[13px] text-foreground">
                          {mov.responsable || '-'}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-[13px] text-foreground">
                          {formatGTDate(mov.fecha)}
                        </TableCell>
                        <TableCell className="px-4 py-3 font-mono text-[13px] font-medium text-emerald-500">
                          {isIngreso ? `Q. ${monto.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}
                        </TableCell>
                        <TableCell className="px-4 py-3 font-mono text-[13px] font-medium text-red-500">
                          {!isIngreso ? `Q. ${monto.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-emerald-500">
                            <CheckCircle className="h-3.5 w-3.5" />
                            <span className="text-xs font-medium">Registrado</span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              title="Ver detalles"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-blue-500"
                              title="Editar"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => { setMovimientoToDelete(mov.id); setDeleteConfirmOpen(true); }}
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Modal Nuevo Movimiento */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-popover border-border sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">Registrar Nuevo Movimiento</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Ingresa los detalles del ingreso o egreso financiero.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label className="text-foreground">Tipo de Movimiento</Label>
              <ToggleGroup
                type="single"
                value={newMovimiento.tipo}
                onValueChange={(value: string) => value && setNewMovimiento({ ...newMovimiento, tipo: value })}
                className="justify-start"
              >
                <ToggleGroupItem
                  value="ingreso"
                  className="border border-input data-[state=on]:bg-emerald-600 data-[state=on]:text-white hover:bg-accent transition flex items-center justify-center gap-2 text-sm font-medium h-9 px-4"
                >
                  <ArrowUpRight className="h-4 w-4" /> Ingreso
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="egreso"
                  className="border border-input data-[state=on]:bg-red-600 data-[state=on]:text-white hover:bg-accent transition flex items-center justify-center gap-2 text-sm font-medium h-9 px-4"
                >
                  <ArrowDownLeft className="h-4 w-4" /> Egreso
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="fecha" className="text-foreground">Fecha</Label>
                <Input
                  id="fecha"
                  type="date"
                  value={newMovimiento.fecha}
                  onChange={(e) => setNewMovimiento({ ...newMovimiento, fecha: e.target.value })}
                  className="bg-secondary border-input focus:border-ring text-foreground font-medium [color-scheme:light] dark:[color-scheme:dark]"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="monto" className="text-foreground">Monto (Q)</Label>
                <Input
                  id="monto"
                  type="number"
                  placeholder="0.00"
                  value={newMovimiento.monto}
                  onChange={(e) => setNewMovimiento({ ...newMovimiento, monto: e.target.value })}
                  className="bg-secondary border-input focus:border-ring text-foreground placeholder:text-muted-foreground"
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="concepto" className="text-foreground">Concepto / Descripción</Label>
              <Input
                id="concepto"
                placeholder="Ej. Pago de servicios, Venta de lote..."
                value={newMovimiento.concepto}
                onChange={(e) => setNewMovimiento({ ...newMovimiento, concepto: e.target.value })}
                className="bg-secondary border-input focus:border-ring text-foreground placeholder:text-muted-foreground"
              />
            </div>

            {/* Campos opcionales para vincular */}
            <div className="grid gap-2">
              <Label htmlFor="responsable" className="text-foreground">Responsable</Label>
              <Input
                id="responsable"
                placeholder="Nombre del responsable"
                value={newMovimiento.responsable}
                onChange={(e) => setNewMovimiento({ ...newMovimiento, responsable: e.target.value })}
                className="bg-secondary border-input focus:border-ring text-foreground"
              />
            </div>

            {/* Campos de documento bancario */}
            <div className="grid grid-cols-2 gap-4 bg-muted/30 p-3 rounded-lg border border-border/50">
              <div className="col-span-2 flex items-center gap-2 mb-1">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground">Datos Bancarios (Opcional)</span>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="numeroDocumento" className="text-xs text-foreground">No. Documento</Label>
                <Input
                  id="numeroDocumento"
                  placeholder="Ej. 123456"
                  value={newMovimiento.numeroDocumento}
                  onChange={(e) => setNewMovimiento({ ...newMovimiento, numeroDocumento: e.target.value })}
                  className="h-8 text-xs bg-secondary border-input focus:border-ring text-foreground"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="transferencia" className="text-xs text-foreground">No. Autorización</Label>
                <Input
                  id="transferencia"
                  placeholder="Ej. 987654"
                  value={newMovimiento.transferencia}
                  onChange={(e) => setNewMovimiento({ ...newMovimiento, transferencia: e.target.value })}
                  className="h-8 text-xs bg-secondary border-input focus:border-ring text-foreground"
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="referencia" className="text-foreground">Referencia Adicional (Opcional)</Label>
              <Input
                id="referencia"
                placeholder="Notas adicionales..."
                value={newMovimiento.referencia}
                onChange={(e) => setNewMovimiento({ ...newMovimiento, referencia: e.target.value })}
                className="bg-secondary border-input focus:border-ring text-foreground placeholder:text-muted-foreground"
              />
            </div>

            {newMovimiento.tipo === 'egreso' && (
              <div className="grid gap-2">
                <Label className="text-foreground">Estado del Egreso</Label>
                <div className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="pagado"
                      name="estado"
                      value="Pagado"
                      checked={newMovimiento.estado === 'Pagado'}
                      onChange={() => setNewMovimiento({ ...newMovimiento, estado: 'Pagado' })}
                      className="text-primary focus:ring-primary"
                    />
                    <Label htmlFor="pagado" className="font-normal cursor-pointer text-foreground">Pagado</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="pendiente"
                      name="estado"
                      value="Pendiente"
                      checked={newMovimiento.estado === 'Pendiente'}
                      onChange={() => setNewMovimiento({ ...newMovimiento, estado: 'Pendiente' })}
                      className="text-primary focus:ring-primary"
                    />
                    <Label htmlFor="pendiente" className="font-normal cursor-pointer text-foreground">Pendiente de Pago</Label>
                  </div>
                </div>
              </div>
            )}

            {formError && <p className="text-sm text-destructive font-medium">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} className="border-input hover:bg-accent text-foreground">
              Cancelar
            </Button>
            <Button onClick={handleSave} className="bg-primary text-primary-foreground hover:bg-primary/90">
              Guardar Movimiento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog >

      {/* Modal Pagar Egreso */}
      < Dialog open={payEgresoOpen} onOpenChange={setPayEgresoOpen} >
        <DialogContent className="bg-popover border-border sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-foreground">Registrar Pago de Egreso</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Confirma el pago del egreso: <b>{egresoToPay?.concepto}</b>
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="fechaPago" className="text-foreground">Fecha de Pago</Label>
              <Input
                id="fechaPago"
                type="date"
                value={payForm.fechaPago}
                onChange={(e) => setPayForm({ ...payForm, fechaPago: e.target.value })}
                className="bg-secondary border-input focus:border-ring text-foreground font-medium [color-scheme:light] dark:[color-scheme:dark]"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="refPago" className="text-foreground">Referencia de Pago</Label>
              <Input
                id="refPago"
                placeholder="No. Cheque, Transferencia..."
                value={payForm.referencia}
                onChange={(e) => setPayForm({ ...payForm, referencia: e.target.value })}
                className="bg-secondary border-input focus:border-ring text-foreground placeholder:text-muted-foreground"
              />
            </div>
            {payError && <p className="text-sm text-destructive font-medium">{payError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayEgresoOpen(false)} className="border-input hover:bg-accent text-foreground">
              Cancelar
            </Button>
            <Button onClick={handlePayEgreso} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              Confirmar Pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog >

      {/* Modal Confirmar Eliminación */}
      < Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen} >
        <DialogContent className="bg-popover border-border sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Confirmar Eliminación
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              ¿Estás seguro de que deseas eliminar este movimiento? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} className="border-input hover:bg-accent text-foreground">
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog >
    </div >
  );
}